import React, {useState, useEffect} from 'react';
import {Box, Typography} from '@mui/material';
import {useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import { SUCCESS, TOAST_SUCCESS, TOAST_ERROR, BUTTON_PRIMARY } from '../assets/constants/Constants';
import { getSecureItemFromSpecificStorage, removeSecureItemFromSpecificStorage, getAndThenRemoveSecureItemFromSpecificStorage } from '../lib/BrowserStorageAccessMiddleware';
import { LOCAL_STORAGE, LS_LOCKER_BANK_INFO, LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS, LS_IS_REDIRECTED_FROM_STRIPE_PAYMENT_GATEWAY, LS_PROP_LOCKER_UNIT_DETAILS, LS_IS_OPEN_WITH_CHECKOUT_AFTER_OVERSTAY_PAYMENT } from '../assets/constants/BrowserStorageKeys';
import {PARCEL_ICON} from '../assets/constants/Icons';
import {SWIPE_TO_OPEN_LOCKER, LOCKER_OPENED} from '../assets/constants/PageList';
import { getPathForNextPage, verifyUserProximity } from '../lib/Utils';
import {openLocker, releaseLockerOfPickUpUser} from '../api/api';
import { postPaymentSaveTransaction } from '../lib/BackendUtils';
import CustomDialog from '../components/CustomDialog';
import CustomSliderComponent from '../components/CustomSliderComponent';
import CircularProgressLoader from '../components/CircularProgressLoader';
import ShowToast from '../components/ToastComponent';

const SwipeToOpenPage = () => {
    const history = useHistory();
    const {t} = useTranslation();
    const lockerBankInfo = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_LOCKER_BANK_INFO);
    const isRedirectedFromStripePaymentGateway = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_IS_REDIRECTED_FROM_STRIPE_PAYMENT_GATEWAY);
    const lockerUnitDetails = history.location?.state?.lockerUnitDetails?.ref_id ? history.location?.state?.lockerUnitDetails : getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_PROP_LOCKER_UNIT_DETAILS);
    console.log('locker unit details 1: ', lockerUnitDetails);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [userProximityError, setUserProximityError] = useState('');
    const [isUserProximityErrorModalVisible, setIsUserProximityErrorModalVisible] = useState(false);
    // const { assignedLocker } = history.location?.state;
    const tenantAndAssignmentSettings = getSecureItemFromSpecificStorage(LOCAL_STORAGE,LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS);
    const isGeoFencingEnabled = tenantAndAssignmentSettings?.is_geofencing;
    const lockerBankLatitude = lockerUnitDetails?.locker_bank?.latitude ? lockerUnitDetails?.locker_bank?.latitude : lockerBankInfo.latitude;
    const lockerBankLongitude = lockerUnitDetails?.locker_bank?.longitude ? lockerUnitDetails?.locker_bank?.longitude : lockerBankInfo.longitude;
    console.log('lat and lng: ', lockerBankLatitude, lockerBankLongitude);
    const canOpenLockerAutomaticallyWhenPageLoads = tenantAndAssignmentSettings?.usecase_settings?.skip_swipe_to_open?.value;
    const isOpenWithCheckoutAfterOverstayPayment = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_IS_OPEN_WITH_CHECKOUT_AFTER_OVERSTAY_PAYMENT);
    removeSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_IS_OPEN_WITH_CHECKOUT_AFTER_OVERSTAY_PAYMENT);
    // console.log('isOpenWithCheckoutAfterOverstayPayment', isOpenWithCheckoutAfterOverstayPayment);
    // console.log('assigned locker: ', assignedLocker);
    // console.log('tna: ', tenantAndAssignmentSettings);
    
    useEffect(() => {
        handleFlagBasedLockerFunctionalitiesUponInitialPageLoad();
        // if (canOpenLockerAutomaticallyWhenPageLoads) {
        //     if (isOpenWithCheckoutAfterOverstayPayment) {
        //         openLockerAndThenCheckout();
        //     }
        //     else {
        //         slideToOpenButtonHandler();
        //     }
        // }
        // else {
        //     setIsLoading(false);
        // }
    }, []);

    const handleFlagBasedLockerFunctionalitiesUponInitialPageLoad = async () => {
        if (isRedirectedFromStripePaymentGateway) {
            await savePostPaymentTransactionDetailsWhenRedirectedFromStripe();
        }

        if (canOpenLockerAutomaticallyWhenPageLoads) {
            if (isOpenWithCheckoutAfterOverstayPayment) {
                openLockerAndThenCheckout();
            }
            else {
                slideToOpenButtonHandler();
            }
        }
        else {
            setIsLoading(false);
        }
    };

    const savePostPaymentTransactionDetailsWhenRedirectedFromStripe = async () => {
        const body = {
            payment_reference_id: lockerUnitDetails?.payment_records[0]?.transaction_id,
            payment_id: lockerUnitDetails?.payment_records[0]?.payment_gateway_reference,
            order_id: "",
            payment_signature: "signature"
        };

        const postPaymentTransactionResponse = await postPaymentSaveTransaction(body);
        console.log('post payment response: ', postPaymentTransactionResponse);

        if (postPaymentTransactionResponse.status === SUCCESS) {
            removeSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_IS_REDIRECTED_FROM_STRIPE_PAYMENT_GATEWAY);
        }
    }

    const slideToOpenButtonHandler = async () => {
        setLoadingMessage(t('opening_locker_please_wait'));
        setIsLoading(true);
        
        if (isGeoFencingEnabled) {
            const maxAllowedDistanceInMeters = tenantAndAssignmentSettings?.geofencing_radius;
            const isUserNearbyLockerBank = await verifyUserProximity({latitude: lockerBankLatitude, longitude: lockerBankLongitude}, maxAllowedDistanceInMeters);

            if ( !isUserNearbyLockerBank.isNearby ) {
                setLoadingMessage('');
                setIsLoading(false);
                setUserProximityError(isUserNearbyLockerBank.message);
                setIsUserProximityErrorModalVisible(true);

                return;
            }
        }

        try {
            const openLockerResponse = await openLocker(lockerUnitDetails?.ref_id);
            if (openLockerResponse.status === SUCCESS) {
                ShowToast(openLockerResponse.message, TOAST_SUCCESS);
                history.replace({
                    pathname: getPathForNextPage(SWIPE_TO_OPEN_LOCKER, LOCKER_OPENED),
                    state: {
                        lockerUnitDetails
                    }
                });
            }
        } catch (error) {
            setIsLoading(false);
            console.error('Error while opening locker: ', error);
        }
    };

    const openLockerAndThenCheckout = async () => {
        setLoadingMessage(t('opening_locker_please_wait'));
        setIsLoading(true);

        sessionStorage.removeItem('isOpenWithCheckoutAfterOverstayPayment');

        if (isGeoFencingEnabled) {
            const maxAllowedDistanceInMeters = tenantAndAssignmentSettings?.geofencing_radius;
            const isUserNearbyLockerBank = await verifyUserProximity({latitude: lockerBankLatitude, longitude: lockerBankLongitude}, maxAllowedDistanceInMeters);

            if ( !isUserNearbyLockerBank.isNearby ) {
                setLoadingMessage('');
                setIsLoading(false);
                setUserProximityError(isUserNearbyLockerBank.message);
                setIsUserProximityErrorModalVisible(true);

                return;
            }
        }

        try {
            const openLockerResponse = await openLocker(lockerUnitDetails?.ref_id);

            if (openLockerResponse?.status === SUCCESS) {
                setLoadingMessage('Checkout in progress. Please wait');

                const releaseLockerResponse = await releaseLockerOfPickUpUser(lockerUnitDetails?.ref_id);

                if (releaseLockerResponse?.status === SUCCESS) {
                    ShowToast(releaseLockerResponse?.message, TOAST_SUCCESS);
                    setIsLoading(false);
                    history.push({
                        pathname: getPathForNextPage(SWIPE_TO_OPEN_LOCKER, LOCKER_OPENED),
                        state: {
                            lockerUnitDetails
                        }
                    });
                }
            }
        } catch (error) {
            ShowToast('Error during open with checkout.', TOAST_ERROR);
            console.error('Error during open with checkout: ', error);
            setIsLoading(false);

            history.push({
                pathname: '/home-screen',
            });
        }
    }
    
     return (
        <div className='swipe-to-open-page'>
            <div className='main-container'>
                {isLoading && (
                    <div className='main'>
                        <CircularProgressLoader message={loadingMessage} />
                    </div>
                )}
                {(!isLoading) && (
                    <div className='main'>
                        <div className='locker-icon-container'>
                            <PARCEL_ICON />
                        </div>
                        <p className='locker-bank-name'>{lockerUnitDetails?.locker_bank?.name}</p>
                        <p className='locker-unit'>{lockerUnitDetails?.locker_door?.door_no}</p>
                        <div className='slide-to-open-button-container'>
                            <CustomSliderComponent
                                onSlideDone={slideToOpenButtonHandler}
                                extraClass={'slide-to-open-button'}
                            />
                        </div>
                        <CustomDialog
                            dialogId='user-proximity-error-modal'
                            dialogVisible={isUserProximityErrorModalVisible}
                            onClose={() => setIsUserProximityErrorModalVisible(false)}
                            handleCancel={() => setIsUserProximityErrorModalVisible(false)}
                            dialogTitle={t('Location Error')}
                            dialogContentText={userProximityError}
                            buttonOneTitle={t('Ok')}
                            buttonProps={{buttonOne: {type: BUTTON_PRIMARY}}}
                        />
                    </div>
                )}
            </div>
        </div>
    );

};

export default SwipeToOpenPage;